import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { ExpandMore } from '@mui/icons-material';
import AddCircle from '@mui/icons-material/AddCircle';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Button as ShadcnButton } from '@repo/videa-components/src/ui/button';
import PropTypes from 'prop-types';

import { usePreferences } from '@/react-query/usePreferences';

import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import { CARIES, RADIOGRAPHIC_BONELOSS } from '../../../../constants';
import {
  blueBlack650,
  blueBlack700,
  blueUltramarine300,
} from '../../../../theme/videa-style-tokens';
import { colors } from '../../../../utils/theme';
import {
  determinePathologyTypeList,
  groupPathologiesByToothNumber,
  isAiEnabledForFindingType,
  shouldTreatImageAsPA,
} from '../../annotation-utils/annotation-utils';
import AnnotatorConstants from '../../annotator.constants';
import { useIsWithinConfidenceThreshold } from '../confidenceScoreFilter/helpers';
import { useFindingSidebarTranslations } from '../constants';
import FindingGroupHeader from '../findingGroupHeader/FindingGroupHeader';

import ImageItem from './imageItem/ImageItem';
import { RBLColorSettings } from './rblColorSettings/RBLColorSettings';
import ToothGroup from './toothGroup/ToothGroup';

const useStyles = makeStyles((theme) => ({
  findingContainer: {
    padding: 0,
    width: '335px',
  },
  findingList: {
    margin: 0,
    padding: 0,
  },
  divider: {
    color: '#f2f2f2',
  },
  spacer: {
    marginBottom: theme.spacing(3),
  },
  findingListItem: {
    borderColor: colors.VERY_LIGHT_GREY,
    height: 52,
    color: 'white',
    '&:hover': {
      background: 'rgba(255,255,255,0.2)',
    },
  },
  listHeader: {
    display: 'flex',
    paddingTop: '16px',
    paddingBottom: '16px',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: 'white',
    zIndex: 0,
    borderWidth: '1px',
    borderColor: blueBlack700,
  },
  percentage: {
    color: 'gray',
    textAlign: 'center',
    display: 'inline',
    paddingLeft: theme.spacing(2),
    fontStyle: 'italic',
  },
  findingLabel: {
    display: 'inline',
  },
  findingNumberLabel: {
    color: colors.PRIMARY,
  },
  noFindingsLabel: {
    padding: theme.spacing(1),
    fontStyle: 'italic',
  },
  p2Left: {
    paddingLeft: theme.spacing(2),
  },
  pad0: {
    padding: 0,
  },
  validationSelectorContainer: {
    display: 'flex',
    justifyContent: 'space-around',
  },
  fontSize18: {
    fontSize: '18px',
  },
  addFindingButton: {
    width: '100%',
    fontFamily: 'proxima-nova, sans-serif',
    backgroundColor: blueBlack650,
  },
  addFindingButtonWhite: {
    width: 'fit-content',
    gap: '.5em',
    fontFamily: 'proxima-nova, sans-serif',
    backgroundColor: blueBlack650,
    padding: '.5em',
    color: 'white',
    borderColor: 'transparent',
  },
  createFindingsBox: {
    fontFamily: 'proxima-nova, sans-serif',
    padding: '.5em 1em',
  },
  createFindingsHeader: {
    color: 'white',
    padding: '.5em',
  },
  createFindingsVideaTypography: {
    padding: '1em .5em',
  },
  createFindingsCaption: {
    color: 'white',
    opacity: 0.33,
    padding: '0 .5em 1.5em .5em',
    lineHeight: '1em',
  },
  pathologyItem: {
    borderTop: `1px solid ${blueBlack700}`,
  },
  rblSubheader: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 1em',

    '& .MuiList-root': {
      paddingBottom: 0,
    },
  },
  rblMeasurementGuide: {
    boxSizing: 'border-box',
    display: 'flex',
    textTransform: 'none',
    color: blueUltramarine300,
    fontSize: '0.5em',
    fontWeight: 'bold',
    textAlign: 'left',
    lineHeight: '2em',
    padding: '0.5em 1em',
  },
  rblColorSettings: {
    fontSize: '0.8em',
    fontWeight: 'bold',
    textAlign: 'left',
    lineHeight: '1.1em',
    padding: '0.5em 0.5em',
    display: 'flex',
    justifyContent: 'space-between',
  },
  rblCheckBox: {
    margin: '0.5em 1em',
    lineHeight: '1.1em',
  },
  rblCheckBoxFont: {
    fontFamily: 'proxima-nova, sans-serif',
  },
}));
const FindingsListContainer = styled.div`
  color: white;
  .MuiAccordion-root {
    background: inherit;
    display: flex;
    flex-direction: column;
  }
  .MuiAccordionSummary-root {
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;
    padding: 0 1.25em;
    gap: 0.5em;
  }
  .MuiAccordionSummary-content {
    display: flex;
  }
  .MuiAccordionSummary-expandIconWrapper {
    width: 1em;
    display: flex;
    svg {
      fill: white;
      width: 0.5em;
      display: flex;
    }
  }
  .MuiAccordionDetails-root {
    display: flex;
    flex-direction: column;
    padding: 0 0.5em;
  }
  .MuiTypography-root {
    color: white;
    display: flex;
  }
  .flex {
    display: flex;
  }
  .box {
    display: flex;
    flex-direction: column;
  }
  .accDetails {
    display: flex;
  }
  .zeroFindings {
    display: flex;
    padding: 0 1.25em;
  }
`;
const FindingList = (props: any) => {
  const {
    enabledFindingTypes = {},
    findingGroup = {},
    sortedAndFilteredImages = [],
    activeImage = undefined,
    setAnchorEls = {},
    viewerMode = 0,
    currentlyHoveredImageId = '',
    user = {},
    shouldDisplayRootTips = true,
    featureFlaggedFindingTypes = {},
    onChangeViewerMode = () => {},
    onHandleSetAnnotationDrawType = () => {},
    onDeleteUserAnnotation = () => {},
    onSetEnabledFindingTypes = () => {},
    onHighlightFinding = () => {},
    onSetCurrentlyHoveredImageId = () => {},
    onUpdateToothNumber = () => {},
    onSetShouldDisplayRootTips = () => {},
    onSetIsRblMeasurementGuideOpen = () => {},
    onUpdateFinding = () => {},
  } = props;
  const isWithinConfidenceThreshold = useIsWithinConfidenceThreshold();
  const { noFindingsDetectedMessage, typeToHeaderMap } =
    useFindingSidebarTranslations();
  const classes = useStyles();
  const findingGroupType = findingGroup.type;
  const isPAImage = shouldTreatImageAsPA(activeImage);
  const { data: prefs } = usePreferences();
  const disabledForPilotMap = {
    boneLoss: 'boneLoss',
  };
  const { t } = useLingui();

  const isFindingTypeVisible =
    enabledFindingTypes[findingGroupType] &&
    featureFlaggedFindingTypes[findingGroupType];

  const isAnImageSelected = !!activeImage?.objectId;

  const allPathologiesToDisplay = determinePathologyTypeList(
    findingGroup.findings,
    findingGroupType,
  );

  const isFindingsGroupAndImageSelected =
    isFindingTypeVisible && isAnImageSelected;

  const isFindingsGroupAndImageNotSelected =
    isFindingTypeVisible && !isAnImageSelected;

  const { canEnable } = findingGroup;
  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  const canToggleFindingType = disabledForPilotMap[findingGroupType]
    ? false
    : canEnable;

  const toothNumberToFindingsMap = groupPathologiesByToothNumber(
    allPathologiesToDisplay,
  );

  const toothNumbersOnImage = Object.keys(toothNumberToFindingsMap);

  const toggleRootTipVisibility = () => {
    onSetShouldDisplayRootTips((prev: any) => !prev);
  };

  const hasRblFinding = findingGroupType === RADIOGRAPHIC_BONELOSS;

  const isModelEnabledForFmx = (currentFindingGroup: any) => {
    const findingTypeEnabled = sortedAndFilteredImages.some(
      (image: any) =>
        !!image.objectId &&
        !!image.models.caries?.modelId &&
        isAiEnabledForFindingType(image, currentFindingGroup.type),
    );
    return currentFindingGroup.findings?.length === 0 && findingTypeEnabled;
  };

  const modelNotRunMessaging = (modelName: any) =>
    isAnImageSelected
      ? t`Analysis for ${modelName} was not included when this image was processed by Videa's AI`
      : t`Analysis for ${modelName} was not included when this image set was processed by Videa's AI`;

  const computeMessagingForNoAiFindings = () => {
    let messageToDisplay;
    if (
      enabledFindingTypes[findingGroupType] &&
      featureFlaggedFindingTypes[findingGroupType] &&
      findingGroup.findings?.length === 0
    ) {
      let hasModelRun = false;

      if (isAnImageSelected) {
        hasModelRun = isAiEnabledForFindingType(activeImage, findingGroupType);
      } else {
        hasModelRun = isModelEnabledForFmx(findingGroup);
      }
      // For now, if we have no caries findings, we cannot be certain that the areas of concern model ran or not
      if (!hasModelRun) {
        messageToDisplay = modelNotRunMessaging(
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          typeToHeaderMap[findingGroupType],
        );
      } else {
        messageToDisplay = noFindingsDetectedMessage;
      }
      return (
        <ListItem>
          <VideaTypography
            variant="bodyExtrasmall"
            className={classes.createFindingsCaption}
          >
            {messageToDisplay}
          </VideaTypography>
        </ListItem>
      );
    }
  };

  const { allFindingsTotal, allFindingsHiddenTotal } =
    sortedAndFilteredImages.reduce(
      (
        accumulator: {
          allFindingsHiddenTotal: number;
          allFindingsTotal: number;
        },
        image: any,
      ) => {
        const findingsForImage = findingGroup.findings.filter(
          (finding: any) =>
            finding.sourceImageId && finding.sourceImageId === image.imageId,
        );
        // For RBL don't count center sides or sides with no points

        let numberOfFindings = findingsForImage.length;
        if (findingGroup.type === RADIOGRAPHIC_BONELOSS) {
          numberOfFindings = findingsForImage.filter(
            (finding: any) =>
              finding.side !== 'center' && finding.points.length >= 2,
          )?.length;
        }

        const numberOfHiddenFindings =
          findingsForImage?.filter((f: any) => {
            let shouldHideFinding = !isWithinConfidenceThreshold(
              f.type,
              f.confidenceScore,
              f?.bin,
            );
            if (!shouldHideFinding && findingGroup.type === CARIES) {
              shouldHideFinding = f.validation === 'Disagree';
            }
            return shouldHideFinding;
          })?.length || 0;
        accumulator.allFindingsHiddenTotal += numberOfHiddenFindings;
        accumulator.allFindingsTotal += numberOfFindings;
        return accumulator;
      },
      { allFindingsTotal: 0, allFindingsHiddenTotal: 0 },
    );

  return (
    <FindingGroupHeader
      findingGroup={findingGroup}
      canEnable={canToggleFindingType}
      enabledFindingTypes={enabledFindingTypes}
      sortedAndFilteredImages={sortedAndFilteredImages}
      user={user}
      onSetEnabledFindingTypes={onSetEnabledFindingTypes}
    >
      <>
        {isFindingsGroupAndImageNotSelected && (
          <FindingsListContainer>
            {allFindingsTotal > 0 ? (
              <Accordion>
                <div className="accDetails">
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <VideaTypography variant="bodyExtrasmall">
                      {findingGroup.type !== RADIOGRAPHIC_BONELOSS ? (
                        <Trans>
                          Findings: {allFindingsTotal - allFindingsHiddenTotal}{' '}
                          of {allFindingsTotal}
                        </Trans>
                      ) : (
                        <Trans>Findings: {allFindingsTotal}</Trans>
                      )}
                    </VideaTypography>
                  </AccordionSummary>
                </div>
                <AccordionDetails>
                  <List>
                    {sortedAndFilteredImages.map(
                      (currentImage: any, imageIndex: any) => {
                        return (
                          currentImage && (
                            <ImageItem
                              image={currentImage}
                              index={imageIndex + 1}
                              key={imageIndex}
                              findingGroup={findingGroup}
                              currentlyHoveredImageId={currentlyHoveredImageId}
                              user={user}
                              onSetCurrentlyHoveredImageId={
                                onSetCurrentlyHoveredImageId
                              }
                            />
                          )
                        );
                      },
                    )}
                  </List>
                </AccordionDetails>
              </Accordion>
            ) : (
              <div className="zeroFindings">
                <VideaTypography variant="bodyExtrasmall">
                  <Trans>Findings: {allFindingsTotal}</Trans>
                </VideaTypography>
              </div>
            )}
          </FindingsListContainer>
        )}

        {isFindingsGroupAndImageSelected && (
          <FindingsListContainer>
            {allFindingsTotal > 0 ? (
              <Accordion>
                <div className="accDetails">
                  <AccordionSummary
                    className={classes.rblSubheader}
                    expandIcon={<ExpandMore />}
                    data-cy={`dropdown-${findingGroup.type}`}
                  >
                    <VideaTypography
                      variant="bodyExtrasmall"
                      className="accDetails"
                    >
                      {findingGroup.type !== RADIOGRAPHIC_BONELOSS ? (
                        <Trans>
                          Findings: {allFindingsTotal - allFindingsHiddenTotal}{' '}
                          of {allFindingsTotal}
                        </Trans>
                      ) : (
                        <Trans>Findings: {allFindingsTotal}</Trans>
                      )}
                    </VideaTypography>
                  </AccordionSummary>
                </div>
                <AccordionDetails>
                  {hasRblFinding && (
                    <Box>
                      <div className={classes.rblColorSettings}>
                        {hasRblFinding &&
                          !prefs?.disablePracticePreferences && (
                            <RBLColorSettings />
                          )}
                        {hasRblFinding && (
                          <Button
                            color="info"
                            className={classes.rblMeasurementGuide}
                            onClick={() => onSetIsRblMeasurementGuideOpen(true)}
                            startIcon={<LaunchIcon />}
                          >
                            <Trans>MEASUREMENT GUIDE</Trans>
                          </Button>
                        )}
                      </div>
                      <Box className={classes.rblCheckBox}>
                        {hasRblFinding && isPAImage && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                color="info"
                                size="small"
                                checked={shouldDisplayRootTips}
                                onChange={toggleRootTipVisibility}
                                inputProps={{ 'aria-label': 'controlled' }}
                              />
                            }
                            label={
                              <VideaTypography
                                variant="labelExtrasmall"
                                className={classes.rblCheckBoxFont}
                              >
                                <Trans>Show Root Tips</Trans>
                              </VideaTypography>
                            }
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                  <List>
                    {toothNumbersOnImage.map((currentToothNumberKey) => {
                      const findingsForTooth =
                        toothNumberToFindingsMap[currentToothNumberKey];
                      // Note: If it's not a videa demo user, it's grouped under an empty tooth number
                      return (
                        <ToothGroup
                          key={currentToothNumberKey}
                          findingsForTooth={findingsForTooth}
                          deleteUserAnnotation={onDeleteUserAnnotation}
                          findingGroup={findingGroup}
                          highlightFinding={onHighlightFinding}
                          isFindingTypeVisible={isFindingTypeVisible}
                          setAnchorEls={setAnchorEls}
                          toothNumber={currentToothNumberKey}
                          updateFinding={onUpdateFinding}
                          updateToothNumber={onUpdateToothNumber}
                          isPAImage={isPAImage}
                          activeImage={activeImage}
                        />
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            ) : (
              <div className="zeroFindings">
                <VideaTypography variant="bodyExtrasmall">
                  <Trans>Findings: {allFindingsTotal}</Trans>
                </VideaTypography>
              </div>
            )}
          </FindingsListContainer>
        )}

        {computeMessagingForNoAiFindings()}

        {isFindingTypeVisible &&
          isAnImageSelected &&
          findingGroupType === CARIES &&
          !!activeImage.fmxIndex && (
            <Box
              className={classes.createFindingsBox}
              display="flex"
              justifyContent="center"
              flexDirection="column"
              p={1}
            >
              <VideaTypography
                variant="bodyExtrasmall"
                className={classes.createFindingsHeader}
              >
                <Trans>Add Findings</Trans>
              </VideaTypography>
              <VideaTypography
                variant="labelExtrasmall"
                className={classes.createFindingsVideaTypography}
              >
                <Trans>Did Videa miss a finding?</Trans>
              </VideaTypography>
              <ShadcnButton
                variant={
                  viewerMode === AnnotatorConstants.MODES.DRAWING
                    ? 'secondary'
                    : 'default'
                }
                data-cy={`createFindingsButton${findingGroup.type}`}
                onClick={() => {
                  onHandleSetAnnotationDrawType(findingGroup?.type);
                  onChangeViewerMode(
                    viewerMode === AnnotatorConstants.MODES.DRAWING
                      ? AnnotatorConstants.MODES.VIEWING
                      : AnnotatorConstants.MODES.DRAWING,
                  );
                }}
                className="vai-w-fit vai-gap-2 vai-font-proxima-nova vai-p-2"
              >
                <AddCircle fontSize="small" />
                {viewerMode === AnnotatorConstants.MODES.DRAWING
                  ? t`Cancel Adding Finding`
                  : t`Create Findings`}
              </ShadcnButton>
            </Box>
          )}
      </>
    </FindingGroupHeader>
  );
};

FindingList.propTypes = {
  enabledFindingTypes: PropTypes.object,
  findingGroup: PropTypes.object,
  setAnchorEls: PropTypes.object,
  viewerMode: PropTypes.number,
  currentlyHoveredImageId: PropTypes.string,
  user: PropTypes.object,
  shouldDisplayRootTips: PropTypes.bool,
  featureFlaggedFindingTypes: PropTypes.object,
  sortedAndFilteredImages: PropTypes.array,
  activeImage: PropTypes.object,
  onChangeViewerMode: PropTypes.func,
  onHandleSetAnnotationDrawType: PropTypes.func,
  onDeleteUserAnnotation: PropTypes.func,
  onSetEnabledFindingTypes: PropTypes.func,
  onHighlightFinding: PropTypes.func,
  onSetCurrentlyHoveredImageId: PropTypes.func,
  onUpdateToothNumber: PropTypes.func,
  onSetShouldDisplayRootTips: PropTypes.func,
  onSetIsRblMeasurementGuideOpen: PropTypes.func,
  onUpdateFinding: PropTypes.func,
};

export default FindingList;
